// @flow

import React from 'react';
import styled from 'styled-components';
import Input from 'antd/lib/input/Input';
import Button from 'antd/lib/button';
import InputNumber from 'antd/lib/input-number';
import DatePicker from 'antd/lib/date-picker';
import { FormikProps } from 'formik';
import moment from 'moment';

import type { Battery } from '../../../../lib/types';
import { Form, OrgUnitSelect } from './../../../../components';
import Select from './../../../../components/ui/Select';
import Grid, { GridItem } from './../../../../components/layout/Grid';
import { convertDateToString } from './../../../../containers/Vehicles/lib';
import MdmInput from '../../../../components/MdmInput';
import type { MdmNode } from '../../../../lib/types/mdm';
import { Section } from './../../../../components/layout';
import CancelButton from '../../../../components/CancelButton';
import MdmSelect from '../../../../components/selects/MdmSelect';

const capacities = [18, 55, 60, 66, 72, 77, 90, 100, 140, 190, 200];

const StyledInputNumber = styled(InputNumber)`
  width: 125px;
`;

const { Option } = Select;

const Content = styled.div`
  padding: 16px;
`;
const Footer = styled(Section)`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export type BlockedMdmFields = {
  capacity: boolean,
  voltage: boolean,
  name: boolean
};

type FormProps = {
  battery: $Shape<Battery>,
  onSubmit: Function,
  onCancel: Function,
  blockMdmFields: Function,
  blockedMdmFields: BlockedMdmFields,
  canEditOrgUnit: boolean,
  vehicleId: ?number,
  isAdmin: boolean
};

const InnerForm = ({
  onCancel,
  onSubmit,
  battery,
  blockedMdmFields,
  blockMdmFields,
  canEditOrgUnit,
  vehicleId,
  isAdmin
}: FormProps) => (
  <Form initialValues={battery} onSubmit={onSubmit}>
    {(FormField, formikProps: FormikProps) => {
      const {
        handleSubmit,
        setFieldValue,
        handleBlur,
        dirty,
        isSubmitting,
        values
      } = formikProps;
      return (
        <form onSubmit={handleSubmit}>
          <Section>
            <Content>
              <Grid gutter="16px">
                {canEditOrgUnit && (
                  <GridItem>
                    <FormField
                      fast
                      label="Подразделение"
                      required
                      name="orgUnitId"
                    >
                      {({ value, name }) => (
                        <OrgUnitSelect
                          value={value || ''}
                          onChange={(orgUnitId: number) =>
                            setFieldValue(name, parseInt(orgUnitId, 10))
                          }
                          data-cy="orgUnitSelect"
                        />
                      )}
                    </FormField>
                  </GridItem>
                )}
                <GridItem>
                  <FormField label="Наименование" required name="name">
                    {field => (
                      <Input
                        {...field}
                        disabled={blockedMdmFields[field.name]}
                      />
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Примечание" name="notation" fast>
                    {field => <Input {...field} />}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Заводской номер" name="factoryNumber" fast>
                    {field => <Input {...field} />}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Марка" name="brand">
                    {field => <Input {...field} data-cy="brandInput" />}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField
                    name="capacity"
                    label="Емкость батареи, А-ч"
                    required
                  >
                    {({ value, name }) => (
                      <Select
                        showSearch
                        optionFilterProp="children"
                        data-cy="capacitySelect"
                        value={value}
                        disabled={blockedMdmFields[name]}
                        onBlur={() => handleBlur({ target: { name } })}
                        onChange={(value: number) => setFieldValue(name, value)}
                        filterOption={(input, option) =>
                          `${option.props.children}`
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {capacities.map((value, index) => (
                          <Option key={index} value={value}>
                            {value}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField
                    label="Напряжение (В)"
                    hasFeedback={false}
                    required
                    name="voltage"
                  >
                    {({ name, value }) => (
                      <StyledInputNumber
                        value={value}
                        disabled={blockedMdmFields[name]}
                        min={1}
                        name={name}
                        onChange={(value: number) => setFieldValue(name, value)}
                        onBlur={handleBlur}
                      />
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField
                    label="Норматив эксплуатации, количество месяцев"
                    required
                    name="norm"
                  >
                    {({ name, value }) => (
                      <StyledInputNumber
                        name={name}
                        disabled
                        value={value}
                        min={1}
                        max={100}
                        onChange={(value: number) =>
                          setFieldValue('norm', value)
                        }
                        onBlur={handleBlur}
                      />
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Номер партии" name="batchNumber" fast>
                    {field => <Input {...field} />}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField label="Мнемокод" name="code" hasFeedback={false}>
                    {field => (
                      <MdmInput
                        {...field}
                        onSelect={(mdmNode: MdmNode) => {
                          const { properties } = mdmNode.payload;
                          setFieldValue(
                            'voltage',
                            properties['Номинальное напряжение, В']
                          );
                          setFieldValue(
                            'capacity',
                            properties['Номинальная емкость, А/ч']
                          );
                          setFieldValue('name', mdmNode.name);
                          blockMdmFields();
                        }}
                      />
                    )}
                  </FormField>
                </GridItem>
                <GridItem>
                  <FormField
                    label="Дата выдачи"
                    required
                    name="issuedDate"
                    fast
                  >
                    {({ name, value }) => (
                      <DatePicker
                        format="DD.MM.YYYY"
                        value={value ? moment(value) : null}
                        onChange={(value: Object, dateString: string) =>
                          setFieldValue(
                            name,
                            convertDateToString(value, dateString)
                          )
                        }
                      />
                    )}
                  </FormField>
                </GridItem>
                {isAdmin && (
                  <GridItem>
                    <FormField
                      label="Дата установки"
                      name="installDate"
                      fast
                      required={!!vehicleId}
                    >
                      {({ name, value }) => (
                        <DatePicker
                          format="DD.MM.YYYY"
                          value={value ? moment(value) : null}
                          onChange={(value: Object, dateString: string) =>
                            setFieldValue(
                              name,
                              convertDateToString(value, dateString)
                            )
                          }
                        />
                      )}
                    </FormField>
                  </GridItem>
                )}
                {isAdmin && (
                  <GridItem>
                    <FormField
                      label="Дата планируемой замены"
                      name="removeDate"
                      fast
                    >
                      {({ name, value }) => (
                        <DatePicker
                          format="DD.MM.YYYY"
                          value={value ? moment(value) : null}
                          onChange={(value: Object, dateString: string) =>
                            setFieldValue(
                              name,
                              convertDateToString(value, dateString)
                            )
                          }
                        />
                      )}
                    </FormField>
                  </GridItem>
                )}
                <GridItem>
                  <FormField
                    label="Аналог акб для расчета бюджета"
                    name="otherBatteryMtrId"
                  >
                    {({ name, value }) => (
                      <MdmSelect
                        value={
                          values?.otherBatteryMtr
                            ? `${values.otherBatteryMtr?.mnemocode} ${values.otherBatteryMtr?.name}`
                            : null
                        }
                        name={name}
                        onChange={(value, option) => {
                          setFieldValue(name, option?.props?.mdm?.id);
                          setFieldValue('otherBatteryMtr', option?.props?.mdm);
                        }}
                      />
                    )}
                  </FormField>
                </GridItem>
                {isAdmin && (
                  <GridItem>
                    <FormField
                      label="Дата списания"
                      name="decomissionDate"
                      fast
                      required={!!vehicleId}
                    >
                      {({ name, value }) => (
                        <DatePicker
                          format="DD.MM.YYYY"
                          value={value ? moment(value) : null}
                          onChange={(value: Object, dateString: string) =>
                            setFieldValue(
                              name,
                              convertDateToString(value, dateString)
                            )
                          }
                        />
                      )}
                    </FormField>
                  </GridItem>
                )}
              </Grid>
            </Content>
          </Section>
          <Footer>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="primary"
              htmlType="submit"
              data-cy="save"
            >
              Сохранить
            </Button>
            <CancelButton dirty={dirty} onClick={onCancel}>
              Отменить
            </CancelButton>
          </Footer>
        </form>
      );
    }}
  </Form>
);

export default InnerForm;
